import { useState, useEffect, useRef } from 'react';
import ValuationForm        from './pages/ValuationForm';
import ResultsScreen        from './pages/ResultsScreen';
import ContactForm          from './pages/ContactForm';
import ValuationDashboard   from './pages/ValuationDashboard';
import ErrorBoundary        from './components/ErrorBoundary';
import { listAnalyses, getAnalysis, submitContact } from './services/api';

const BRAND = 'Digital Centre of Excellence';

// Views: 'form' → 'results' → 'contact' → 'dashboard'


function App() {
  const [view, setView]             = useState('form');
  const [result, setResult]         = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [latest, setLatest]         = useState(null);
  const [analyses, setAnalyses]     = useState([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [currency, setCurrency]     = useState('USD');
  const [showConfirm, setShowConfirm] = useState(false);
  const [cookieDismissed, setCookieDismissed] = useState(
    () => localStorage.getItem('cookie_consent') !== null
  );

  function acceptCookies() {
    localStorage.setItem('cookie_consent', 'accepted');
    setCookieDismissed(true);
  }
  function declineCookies() {
    localStorage.setItem('cookie_consent', 'declined');
    setCookieDismissed(true);
  }

  async function fetchDashboardData(analysisId) {
    setDashLoading(true);
    try {
      const [detail, list] = await Promise.all([
        getAnalysis(analysisId),
        listAnalyses(),
      ]);
      setLatest(detail);
      setAnalyses(list.results || []);
    } catch (err) {
      console.error('Dashboard data fetch failed:', err);
    } finally {
      setDashLoading(false);
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleResult(r) {
    setResult(r);
    setCurrency(r.currency || 'USD');
    setView('results');
  }

  function handleGetDeeper() {
    setView('contact');
  }

  async function handleContactSubmit(info) {
    setContactInfo(info);
    try {
      await submitContact({ analysisId: result?.analysis_id, name: info.name, email: info.email, website: info.website });
    } catch (err) {
      console.error('Contact submission failed:', err);
    }
    setShowConfirm(true);
    setView('dashboard');
    if (result?.analysis_id) fetchDashboardData(result.analysis_id);
    setTimeout(() => setShowConfirm(false), 5000);
  }

  function handleViewDashboard() {
    setView('dashboard');
    if (result?.analysis_id) fetchDashboardData(result.analysis_id);
  }

  function handleViewDetail(id) {
    getAnalysis(id).then(setLatest).catch(err => console.error('Detail fetch failed:', err));
  }

  function handleReset() {
    setView('form');
    setResult(null);
    setContactInfo(null);
  }

  // ── Dashboard (full-width layout) ─────────────────────────────────────────

  if (view === 'dashboard') {
    return (
      <>
        <SiteHeader />
        <div className="app">
          <ErrorBoundary onReset={handleReset}>
            <ValuationDashboard
              latest={latest}
              analyses={analyses}
              loading={dashLoading}
              currency={currency}
              onNewAnalysis={handleReset}
              onViewDetail={handleViewDetail}
            />
          </ErrorBoundary>
          <SiteFooter />
        </div>
        {showConfirm && <SubmitConfirmOverlay name={contactInfo?.name} />}
        {!cookieDismissed && <CookieBanner onAccept={acceptCookies} onDecline={declineCookies} />}
      </>
    );
  }

  // ── Main layout ────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader />
      <div className="app">
        <main className="page-content">
          <ErrorBoundary onReset={handleReset}>

            {/* ── Step 1: Form ── */}
            {view === 'form' && (
              <div className="card">
                <ValuationForm
                  onResult={handleResult}
                  currency={currency}
                  onCurrencyChange={setCurrency}
                />
              </div>
            )}

            {/* ── Step 2: Results ── */}
            {view === 'results' && result && (
              <div className="card card-wide">
                <ResultsScreen
                  result={result}
                  currency={currency}
                  onGetDeeper={handleGetDeeper}
                  onReset={handleReset}
                />
              </div>
            )}

            {/* ── Step 3: Contact form ── */}
            {view === 'contact' && (
              <div className="card">
                <ContactForm
                  onBack={() => setView('results')}
                  onSubmit={handleContactSubmit}
                />
              </div>
            )}


          </ErrorBoundary>
        </main>
        <SiteFooter />
      </div>
      {!cookieDismissed && <CookieBanner onAccept={acceptCookies} onDecline={declineCookies} />}
    </>
  );
}

// ─── Submit Confirmation Overlay ─────────────────────────────────────────────

function SubmitConfirmOverlay({ name }) {
  return (
    <div className="confirm-overlay" role="status" aria-live="polite">
      <div className="confirm-overlay-box">
        <div className="confirm-overlay-icon">✓</div>
        <p className="confirm-overlay-title">You're on the list!</p>
        <p className="confirm-overlay-sub">
          {name ? `Thanks, ${name.split(' ')[0]}. ` : ''}
          We've received your details and will be in touch soon.
        </p>
      </div>
    </div>
  );
}

// ─── Site Chrome (Drupal header/footer) ───────────────────────────────────────

function SiteChrome({ section }) {
  const [html, setHtml] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    fetch(`/api/site-chrome/${section}/`)
      .then(r => r.json())
      .then(data => setHtml(data.html || ''))
      .catch(() => {});
  }, [section]);

  // After the header renders, measure its actual height and push page content down
  useEffect(() => {
    if (section !== 'header' || !ref.current) return;
    function apply() {
      const h = ref.current ? ref.current.offsetHeight : 0;
      if (h > 0) document.documentElement.style.setProperty('--drupal-header-h', h + 'px');
    }
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [html, section]);

  // Wire up the Bulma hamburger toggle (Drupal JS isn't loaded here)
  useEffect(() => {
    if (section !== 'header' || !ref.current) return;
    const burger = ref.current.querySelector('.navbar-burger');
    if (!burger) return;
    function toggle() {
      const targetId = burger.dataset.target;
      const target = document.getElementById(targetId);
      burger.classList.toggle('is-active');
      if (target) target.classList.toggle('is-active');
    }
    burger.addEventListener('click', toggle);
    return () => burger.removeEventListener('click', toggle);
  }, [html, section]);

  if (!html) return null;
  return (
    <div
      ref={ref}
      style={{ width: '100%', display: 'block' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SiteHeader() {
  return <SiteChrome section="header" />;
}

function SiteFooter() {
  return <SiteChrome section="footer" />;
}

// ─── Cookie Consent Banner ────────────────────────────────────────────────────

function CookieBanner({ onAccept, onDecline }) {
  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <p className="cookie-text">
        We store your valuation inputs to power your progress dashboard. No data is sold or shared.
        {' '}<a href="/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' '}·{' '}
        <a href="/terms/" target="_blank" rel="noopener noreferrer">Terms of Use</a>
      </p>
      <div className="cookie-actions">
        <button className="cookie-btn-secondary" onClick={onDecline}>Decline</button>
        <button className="cookie-btn-primary"   onClick={onAccept}>Accept</button>
      </div>
    </div>
  );
}

export default App;
